import { faBars } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Link } from "gatsby"
import React, { useState, useEffect } from "react"
import { useUIToolsContext } from "../contexts/UIToolsProvider"
import { useLanguage } from "../contexts/LanguageContext"
import Dropdown from "../lib/Dropdown"

function Navbar({ location }) {
  const { setMobileNavbarOpen } = useUIToolsContext()
  const { language, setLanguage, t } = useLanguage()
  const [scrolled, setScrolled] = useState(false)

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40)

    onScroll()

    window.addEventListener("scroll", onScroll, { passive: true })

    return () => window.removeEventListener("scroll", onScroll)
  }, [])

  const isActive = (path) => location?.pathname === path

  const langLabels = {
    en: "EN",
    ar: "AR",
    nl: "NL",
  }

  const aboutItems = {
    title: t("navAbout"),
    items: [
      {
        text: t("navWhoAreWe"),
        path: "/about",
      },
      {
        text: t("navCompanyStructure"),
        path: "/about/structure",
      },
      {
        text: t("navVisionMissionValues"),
        path: "/about/vision",
      },
      {
        text: t("navInternationalApproval"),
        path: "/about/international-approval",
      },
      {
        text: t("navHalalMeaning"),
        path: "/about/halal-meaning",
      },
      {
        text: t("navLocations"),
        path: "/about/locations",
      },
    ],
  }

  const certificationItems = {
    title: t("navCertification"),
    items: [
      {
        text: t("navHalalCertificationProcess"),
        path: "/certification/procedure",
      },
      {
        text: t("navProductCategories"),
        path: "/certification/categories",
      },
      {
        text: t("navOurServices"),
        path: "/certification/services",
      },
      {
        text: t("navAuditingTraining"),
        path: "/certification/auditing",
      },
      {
        text: t("navCertificationBenefits"),
        path: "/certification/benefits",
      },
      {
        text: t("navCertificationTerms"),
        path: "/certification/terms",
      },
      {
        text: t("navBpjphRecognition"),
        path: "/certification/bpjph-recognition",
      },
    ],
  }

  const policiesItems = {
    title: t("navPolicies"),
    items: [
      {
        text: t("navHalalGuarantee"),
        path: "/policies/guarantee",
      },
      {
        text: t("navIntegrityPolicy"),
        path: "/policies/integrity",
      },
      {
        text: t("navHandlingAppeals"),
        path: "/policies/handling-appeals-and-complaints",
      },
      {
        text: t("navFeeRegulation"),
        path: "/policies/fee-regulation",
      },
      {
        text: t("navConfidentialityImpartiality"),
        path: "/policies/confidentiality-and-impartiality/",
      },
      {
        text: t("navNonDiscriminatory"),
        path: "/policies/non-discriminatory-condition",
      },
    ],
  }

  const contactItems = {
    title: t("navContact"),
    items: [
      {
        text: t("navRequestInformation"),
        path: "/contact/information",
      },
      {
        text: t("navFeedback"),
        path: "/contact/feedback",
      },
      {
        text: t("navComplaint"),
        path: "/contact/complaint",
      },
      {
        text: t("navCertificateCheck"),
        path: "/contact/check",
      },
      {
        text: t("navSaudiExportForm"),
        path: "/contact/saudi-apply",
      },
    ],
  }

  return (
    <div
      className={`z-30 fixed w-full transition-all duration-300 bg-slate-950/70 backdrop-blur-sm ${
        scrolled
          ? "bg-slate-950/95 backdrop-blur-md shadow-lg shadow-black/20"
          : ""
      }`}
    >
      <div className="flex justify-between items-center w-full max-w-[1400px] m-auto px-4 sm:px-6 py-3.5">

        <Link
          to="/"
          className="flex items-center shrink-0"
        >
          <h2 className="font-poppins font-semibold text-white text-lg scr1100:text-xl uppercase tracking-tight whitespace-nowrap">
            Halal Correct Certification
          </h2>
        </Link>

        <button
          className="min-[950px]:hidden text-white"
          onClick={() => setMobileNavbarOpen(true)}
          aria-label="Open menu"
        >
          <FontAwesomeIcon icon={faBars} size="lg" />
        </button>

        <ul className="hidden min-[950px]:flex items-center gap-1 text-sm list-none">

          <NavItem
            to="/"
            active={isActive("/")}
          >
            {t("navHome")}
          </NavItem>

          <li>
            <Dropdown
              {...aboutItems}
              path="/about"
              active={location?.pathname.indexOf("/about") === 0}
            />
          </li>

          <li>
            <Dropdown
              {...certificationItems}
              path="/certification"
              active={location?.pathname.indexOf("/certification") === 0}
            />
          </li>

          <li>
            <Dropdown
              {...policiesItems}
              path="/policies"
              active={location?.pathname.indexOf("/policies") === 0}
            />
          </li>

          <li>
            <Dropdown
              {...contactItems}
              path="/contact"
              active={location?.pathname.indexOf("/contact") === 0}
            />
          </li>

          <NavItem
            to="/blog"
            active={isActive("/blog")}
          >
            {t("navBlog")}
          </NavItem>

        </ul>

        <div className="hidden min-[950px]:flex items-center gap-3">

          <div className="flex items-center gap-1 bg-white/5 border border-white/10 rounded-full p-1">

            {["en", "ar", "nl"].map((code) => (
              <button
                key={code}
                onClick={() => setLanguage(code)}
                className={`font-poppins font-semibold text-xs uppercase px-2.5 py-1 rounded-full transition-colors duration-300 ${
                  language === code
                    ? "bg-teal-500 text-slate-950"
                    : "text-white/70 hover:text-white"
                }`}
              >
                {langLabels[code]}
              </button>
            ))}

          </div>

          <Link
            to="/contact/apply"
            className="px-5 py-2 rounded-full bg-teal-500 hover:bg-teal-400 hover:scale-105 font-poppins font-semibold text-slate-950 text-xs uppercase tracking-wide transition-all duration-300"
          >
            {t("applyNow")}
          </Link>

        </div>

      </div>
    </div>
  )
}

export default Navbar

function NavItem({ to, active, children }) {
  return (
    <li className="relative px-3 py-2">
      <Link
        to={to}
        className={`font-open font-normal transition-colors duration-300 ${
          active
            ? "text-teal-400"
            : "text-white hover:text-teal-300"
        }`}
      >
        {children}
      </Link>

      {active && (
        <span className="absolute left-3 right-3 -bottom-0.5 h-0.5 bg-teal-400 rounded-full" />
      )}
    </li>
  )
}